<template>
  <div class="tag-details">
    <div
      v-if="!shouldShowResultsPerPerson && lineChartData.data?.length > 1"
      class="d-flex justify-content-center align-items-center mb-3"
    >
      <div class="btn-group">
        <button
          type="button"
          class="btn btn-sm"
          :class="
            selectedTab === 'overall'
              ? 'btn-primary btn-active'
              : 'btn-secondary'
          "
          @click="setSelectedTab('overall')"
        >
          {{ $t('resultDetails.tagDetails.toggleButton.overview') }}
        </button>
        <button
          type="button"
          class="btn btn-sm"
          :class="
            selectedTab === 'trend' ? 'btn-primary btn-active' : 'btn-secondary'
          "
          @click="setSelectedTab('trend')"
        >
          {{ $t('resultDetails.tagDetails.toggleButton.history') }}
        </button>
      </div>
    </div>
    <div class="mt-0 mb-0 d-flex justify-content-center mt-5">
      <h4 class="tag-value">
        {{ currentValue }}
      </h4>
    </div>
    <div
      v-if="
        comparedToBenchmarkText &&
        !shouldShowResultsPerPerson &&
        !shouldBeZeroToHundred
      "
      class="d-flex justify-content-center"
    >
      <p class="puls-benchmark-bubble">{{ comparedToBenchmarkText }}</p>
    </div>
    <div v-if="!shouldShowResultsPerPerson" class="row mt-2 gy-4">
      <div v-if="selectedTab === 'overall'" class="col col-12">
        <BarChart
          :dataName="`tag-details-bar-${tag.name}`"
          :data="barChartData"
          :options="{ useOnlyPercentage: true }"
          :numberOfResponses="tag.numberOfResponses"
          :theme="theme"
        />
      </div>
      <div
        v-if="selectedTab === 'trend' && lineChartData.data?.length > 1"
        class="col col-12"
      >
        <LineChart
          :dataName="`tag-details-line-${tag.name}`"
          :data="lineChartData.data"
          :series="lineChartData.series"
          :options="{ ...lineChartData.options }"
        />
        <div class="alert alert-secondary text-dark mt-15">
          {{ trendText }}
        </div>
      </div>
    </div>
    <div v-else class="row mt-2 gy-4">
      <div class="d-flex justify-content-center my-5 w-100">
        <div
          v-for="resultName in Object.keys(donutDataPerResult)"
          :key="resultName"
          class="flex-grow-1 p-2 results-per-user"
        >
          <DonutChart
            :donutData="donutDataPerResult[resultName]"
            :tag="{ ...tag, name: `tag-${tag.name}-${resultName}` }"
            :theme="theme"
            :userIsAdminForCurrentGroup="
              tag.userIsAdminForCurrentGroupOrSuperAdmin
            "
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BarChart from '@/components/shared/charts/BarChart.vue'
import LineChart from '@/components/shared/charts/LineChart.vue'
import DonutChart from '@/components/shared/charts/DonutChart.vue'
import { getDonutDataForStatItem } from '@/components/shared/charts/chart-helpers'

import { getTrendTextForStatItem } from '@/helpers/trend-helpers'
import { getComparedToBenchmarkText } from '@/helpers/results-helper'
import {
  getLineChartDataForStatItems,
  getBarChartDataForStatItem
} from '@/components/shared/charts/chart-helpers'

export default {
  name: 'TagDetails',
  components: {
    BarChart,
    LineChart,
    DonutChart
  },
  props: {
    tag: {
      type: Object,
      default: () => {}
    },
    resultsPerPerson: {
      type: [Object, Boolean],
      default: () => {}
    },
    shouldBeZeroToHundred: {
      type: Boolean,
      default: false
    },
    shouldShowResultsPerPerson: {
      type: Boolean,
      default: false
    },
    theme: {
      type: String,
      default: 'default'
    },
    onSelectedTabChange: {
      type: Function,
      default: () => {}
    },
    selectedTab: {
      type: String,
      default: 'overall'
    }
  },
  data() {
    return {}
  },
  computed: {
    barChartData() {
      return getBarChartDataForStatItem({ statItem: this.tag })
    },
    donutDataPerResult() {
      return Object.keys(this.resultsPerPerson || {}).reduce((res, result) => {
        const donutDataForResult = {
          ...getDonutDataForStatItem({
            name: `tag-${this.tag.name}-${result}`,
            statItem: { ...this.tag, resultName: result },
            resultsPerPerson: this.resultsPerPerson,
            title: this.$t(
              'resultDetails.tagDetails.color.title',
              this.resultsPerPerson[result]
            ),
            description: this.$t(
              `resultDetails.tagDetails.color.description.${result}`,
              this.resultsPerPerson[result]
            ),
            theme: this.theme,
            localeNumber: this.$localeNumber,
            t: this.$t
          }),
          trend: {
            hasTrend: false
          }
        }
        if (donutDataForResult.currentValue) {
          res[result] = donutDataForResult
        }
        return res
      }, {})
    },
    isEnps() {
      return this.tag.name === 'enps'
    },
    lineChartData() {
      return getLineChartDataForStatItems({
        statItems: { [this.tag.name]: this.tag },
        shouldFormatZeroToHundred: this.shouldBeZeroToHundred,
        isEnps: this.isEnps,
        hasLinearRegression: this.tag.regressionValues?.length > 2,
        tr: this.$tr
      })
    },
    comparedToBenchmarkText() {
      return getComparedToBenchmarkText({
        t: this.$t,
        tr: this.$tr,
        localeNumber: this.$localeNumber,
        item: this.tag,
        shouldBeZeroToHundred: this.shouldBeZeroToHundred || this.isEnps
      })
    },
    currentValue() {
      if (this.tag.value) {
        const fractionalDigits =
          this.isEnps || this.shouldBeZeroToHundred ? 0 : 1
        let valueString = this.tag.value
        if (this.isParticipationTag) {
          valueString = `${valueString}%`
        }
        if (this.shouldBeZeroToHundred) {
          valueString = valueString * 10
        }
        valueString = this.$localeNumber(valueString, fractionalDigits)
        return valueString
      }
      return null
    },
    isParticipationTag() {
      return this.tag.name === 'participation'
    },
    trendText() {
      return getTrendTextForStatItem({
        title: this.$tr(this.tag.messages, 'title'),
        statItem: this.tag,
        t: this.$t
      })
    }
  },
  mounted() {
    if (this.shouldShowResultsPerPerson) {
      // this.selectedTab = 'resultsPerPerson'
      this.onSelectedTabChange('resultsPerPerson')
    } else if (
      !this.lineChartData.data?.length &&
      this.selectedTab === 'trend'
    ) {
      this.onSelectedTabChange('overall')
    }
  },
  methods: {
    getDonutDataPerPerson() {},
    setSelectedTab(tabName) {
      this.onSelectedTabChange(tabName)
    }
  }
}
</script>

<style scoped>
.tag-details {
  min-height: 100%;
}
</style>
